// @flow

import { useMemo, useState } from 'react';

import clsx from 'clsx';
import { Check, Plus } from 'lucide-react';
import { useDispatch } from 'react-redux';
import { v4 } from 'uuid';

import {
  addCartProduct,
  removeCartProduct,
  getCart,
  selectDomainFromFqdn,
} from '@gandi/cart';
import { mapTags, useTranslator } from '@gandi/translate';
import { Base, Fqdn, Spinner } from '@gandi/ui';

import { selectHostingFromFqdn } from 'src/WebHosting/modules/selectors';

import {
  useSelfPromotionTracking,
  type SelfPromotionProps,
} from 'src/hooks/tracking';
import { useAppSelector } from 'src/hooks/useAppSelector';
import { useIsEligibleForXsell } from 'src/hooks/useEligibleCustomer';
import { useNotifications } from 'src/hooks/useNotifications';

import { HostingPackPrice } from './HostingPackPrice';
import { XsellButton } from './XsellButton';
import { useProductData } from './modules';

import styles from './XsellHosting.scss';

type Props = {
  className: string,
  fqdn: string,
  isLoading: boolean,
};

type ProductProps = {
  product: string | React.Node,
  type: 'plus' | 'check',
};
function Product({ product, type }: ProductProps): React.MixedElement {
  const { __ } = useTranslator();
  const Icon = type === 'plus' ? Plus : Check;
  return (
    <Base flex flexAlignItemsCenter gap="spacing-2x" weight="regular" flexNone>
      <Base
        flex
        flexJustifyCenter
        flexAlignItemsCenter
        className={styles[type]}
      >
        <Icon size={16} color="white">
          <title>{type === 'plus' ? '+' : __('Included:')}</title>
        </Icon>
      </Base>
      {product}
    </Base>
  );
}

export function XsellHosting({
  className,
  fqdn,
  isLoading,
}: Props): null | React.MixedElement {
  const dispatch = useDispatch();
  const { __ } = useTranslator();
  const { notifyMaybeApiError } = useNotifications();

  // useMemo is useful do avoid packId regeneration at each rendering
  const packId = useMemo(() => v4(), []);

  const productData = useProductData({ fqdn, packId });

  const [workingSHS, setWorkingSHS] = useState(false);

  const domainProduct = useAppSelector(state =>
    selectDomainFromFqdn(state, fqdn),
  );
  const hostingProduct = useAppSelector(state =>
    selectHostingFromFqdn(state, fqdn),
  );

  const { isEligibleForXsell } = useIsEligibleForXsell(fqdn);

  const offerSize =
    typeof productData?.payload?.size === 'string'
      ? productData.payload.size
      : undefined;
  const tld = domainProduct?.payload?.tld;
  const onsiteAdParams: SelfPromotionProps = useMemo(
    () =>
      isEligibleForXsell &&
      // Only track the xsell impression if it's still displaying a domainProduct result,
      // don't track it again once it's added as a hostingProduct.
      !!tld && {
        campaign: 'xsell_hosting',
        category: offerSize,
        variant: tld,
        general_placement: 'shop_suggest',
        detailed_placement: 'provided_row',
      },
    [isEligibleForXsell, offerSize, tld],
  );

  // Track impression on bundle render
  const { sendSelfPromotionClick } = useSelfPromotionTracking(onsiteAdParams);

  if (!isEligibleForXsell) {
    return null;
  }

  async function handleClick() {
    setWorkingSHS(true);
    try {
      if (hostingProduct) {
        await dispatch(
          removeCartProduct(hostingProduct.id, {
            headers: { 'X-Dry-Run': 0 },
            replaceId: hostingProduct.payload.replace_id,
          }),
        );
      } else {
        await dispatch(
          addCartProduct({
            productData,
            options: {
              headers: { 'X-Dry-Run': 0 },
              replaceId: domainProduct?.id,
            },
          }),
        );

        // Track click when adding bundle to cart
        sendSelfPromotionClick();
      }
    } catch (e) {
      dispatch(notifyMaybeApiError(e));
      Promise.reject();
    } finally {
      setWorkingSHS(false);
    }

    dispatch(getCart());
  }

  const products: Array<{
    key: string,
    ...ProductProps,
  }> = [
    {
      key: 'domain',
      product: hostingProduct ? (
        <Fqdn color="white">{fqdn}</Fqdn>
      ) : (
        __('Domain')
      ),
      type: 'check',
    },
    {
      key: 'hosting',
      product: __('Hosting'),
      type: 'plus',
    },
    {
      key: 'emails',
      product: __('Emails'),
      type: 'plus',
    },
  ];

  return (
    <Base className={clsx(styles.root, className)}>
      <div className={styles.container}>
        <span>
          {mapTags(
            hostingProduct
              ? __('Great! [[info:Hosting & email are included.]]')
              : __('Upgrade this [[info:Domain with Hosting & Email]]'),
            ({ info }) => ({
              info: <strong>{info}</strong>,
            }),
          )}
        </span>

        <div className={styles.priceContainer}>
          <div className={styles.products}>
            {products.map(product => (
              <Product
                key={product.key}
                product={product.product}
                type={product.type}
              />
            ))}
          </div>
          <HostingPackPrice fqdn={fqdn} />
        </div>
      </div>

      {isLoading ? (
        <Spinner theme="dark" name="dots" />
      ) : (
        <XsellButton
          SHSAdded={!!hostingProduct}
          handleClick={handleClick}
          loading={workingSHS}
          disabled={isLoading}
        />
      )}
    </Base>
  );
}
